/**
 * Erreurs métier.
 *
 * Elles traversent les trois portes : un agent doit pouvoir les lire et
 * décider quoi faire, un patron doit pouvoir les comprendre. D'où un code
 * stable pour la machine et un message en français pour l'humain — et jamais
 * de trace d'exécution, qui renseignerait un attaquant sans aider personne.
 */

export type DomainErrorCode =
  | "not_authenticated"
  | "forbidden_role"
  | "confirmation_required"
  | "confirmation_expired"
  | "untrusted_content"
  | "not_found"
  | "invalid_input"
  | "conflict"
  | "quota_exceeded";

export class DomainError extends Error {
  readonly code: DomainErrorCode;
  /** Contexte exploitable par un agent pour formuler sa question suivante. */
  readonly details?: Record<string, unknown>;

  constructor(
    code: DomainErrorCode,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "DomainError";
    this.code = code;
    this.details = details;
  }
}

export function forbiddenRole(
  role: string,
  level: string,
  action: string,
): DomainError {
  return new DomainError(
    "forbidden_role",
    `Le rôle « ${role} » ne peut pas exécuter « ${action} ». ` +
      `Cette action est de niveau ${level} et demande un patron.`,
    { role, level, action },
  );
}

export function confirmationRequired(
  action: string,
  summary: string,
): DomainError {
  return new DomainError(
    "confirmation_required",
    `« ${action} » a un effet vers l'extérieur et demande une confirmation. ` +
      `Résumé à présenter : ${summary}`,
    { action, summary },
  );
}

export function untrustedContent(action: string): DomainError {
  return new DomainError(
    "untrusted_content",
    `« ${action} » ne peut pas être déclenchée par du contenu externe ` +
      `(e-mail, PDF, message reçu). Une confirmation humaine est obligatoire.`,
    { action },
  );
}
